import { Link } from 'react-router-dom';

import {
  getNotificationListQueryKeyPrefix,
  queryClient,
  useNotificationsQuery,
} from '@frontend/api';
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverFooter,
  Badge,
  HStack,
  NotificationsIcon,
} from '@frontend/components';

import HeaderIconButton from 'components/HeaderIconButton/HeaderIconButton';

import MarkAllNotificationsReadButton from './components/MarkAllNotificationsReadButton';
import NotificationsList from './components/NotificationsList';
import { PopoutContext } from './hooks/PopoutContext.js';

const NotificationsPopout = () => {
  const { data } = useNotificationsQuery({});
  const notifications = data?.results || [];
  const totalNotificationsCount = data?.count;
  const unacknowledgedNotificationsCount = data?.unacknowledgedCount;

  const badgeText =
    unacknowledgedNotificationsCount > 999
      ? '999+'
      : unacknowledgedNotificationsCount;

  // Space the button out by 1/2 unit per visible digit in the count
  // 1 = 4px
  // 10 = 8px
  // 100 = 12px
  const buttonMargin =
    Math.min(unacknowledgedNotificationsCount, 999)?.toString().length / 2;

  return (
    <Popover gutter={12} placement="bottom-end">
      {({ isOpen, onClose }) => (
        <>
          <PopoverTrigger>
            <HeaderIconButton position="relative" mr={buttonMargin}>
              <NotificationsIcon size={4} />
              {unacknowledgedNotificationsCount > 0 && (
                <Badge
                  borderRadius="full"
                  colorScheme="error"
                  position="absolute"
                  top={0}
                  left="100%"
                  transform="translate(-50%, 0%)"
                >
                  {badgeText}
                </Badge>
              )}
            </HeaderIconButton>
          </PopoverTrigger>
          <PopoverContent width={44} minWidth={0}>
            <PopoverHeader gap={1} minWidth={0}>
              <HStack flexGrow={1} justifyContent="space-between">
                <span>Notifications</span>
                {totalNotificationsCount > 0 && (
                  <MarkAllNotificationsReadButton
                    callback={() => {
                      queryClient.invalidateQueries({
                        queryKey: getNotificationListQueryKeyPrefix(),
                      });
                      onClose();
                    }}
                  />
                )}
              </HStack>
            </PopoverHeader>
            <PopoverBody p={0} maxHeight="calc(100vh - 150px)" overflowY="auto">
              <PopoutContext.Provider
                value={{ isPopout: isOpen, closePopout: () => onClose() }}
              >
                <NotificationsList notifications={notifications} />
              </PopoutContext.Provider>
            </PopoverBody>
            <PopoverFooter justifyContent="center">
              <Button
                as={Link}
                size="md"
                variant="link"
                to="/notifications"
                onClick={onClose}
              >
                View All
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

export default NotificationsPopout;
